import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, Loading, PageHeader, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, CalendarDays, Swords, ClipboardCheck } from "lucide-react";

const SEL_OPTS = [["selected", "Selected"], ["not_selected", "Not Selected"], ["pending", "Pending"], ["unavailable", "Unavailable"]];

export default function CoachPortal() {
  const { data, loading, refetch } = useFetch("/portal/coach", []);
  const nav = useNavigate();
  const [squad, setSquad] = useState(null); // {match, players, sel}
  if (loading) return <Loading />;
  const teams = data?.teams || [];
  const trainings = data?.trainings || [];
  const matches = data?.matches || [];
  const playersByTeam = Object.fromEntries(teams.map((t) => [t.id, t.players || []]));

  const openSquad = async (m) => {
    let existing = [];
    try { const r = await client.get(`/matches/${m.id}/selections`); existing = r.data.selections || []; } catch (e) {}
    const map = Object.fromEntries(existing.map((s) => [s.player_id, s.status]));
    setSquad({ match: m, players: playersByTeam[m.team_id] || [], sel: map });
  };
  const saveSquad = async () => {
    const selections = Object.entries(squad.sel).map(([player_id, status]) => ({ player_id, status }));
    try { await client.put(`/matches/${squad.match.id}/selections`, { selections }); toast.success("Squad saved — parents notified"); setSquad(null); refetch(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="My Teams" subtitle="Your assigned teams, players and schedule" testid="coach-portal">
        <Button variant="outline" onClick={() => nav("/training")} data-testid="coach-attendance"><ClipboardCheck size={16} className="me-1" /> Take Attendance</Button>
      </PageHeader>
      {teams.length === 0 && <Card className="p-10 text-center text-muted-foreground">No teams are assigned to you yet.</Card>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {teams.map((t) => (
          <Card key={t.id} className="p-6" data-testid={`coach-team-${t.id}`}>
            <div className="flex items-center justify-between mb-4">
              <div><h2 className="text-lg font-extrabold font-heading">{t.name}</h2><div className="flex gap-2 mt-1"><StatusBadge status={t.category} /><span className="text-sm text-muted-foreground">{t.player_count} players</span></div></div>
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center"><Users size={20} /></div>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto scrollbar-thin">
              {(t.players || []).length === 0 && <div className="p-4 text-sm text-muted-foreground">No players</div>}
              {(t.players || []).map((p) => (
                <button key={p.id} onClick={() => nav(`/players/${p.id}`)} className="w-full text-start flex justify-between items-center px-4 py-2 text-sm hover:bg-muted">
                  <span className="font-medium">{p.full_name}</span>
                  <span className="text-muted-foreground text-xs">{p.player_code} · {p.position || "—"}</span>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6">
          <h3 className="font-bold mb-3 flex items-center gap-2"><CalendarDays size={16} /> Upcoming Trainings</h3>
          {trainings.length === 0 && <p className="text-sm text-muted-foreground">None scheduled</p>}
          {trainings.slice(0, 8).map((tr) => <div key={tr.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0"><span>{tr.title || "Training"}</span><span className="text-muted-foreground">{tr.date} {tr.time}</span></div>)}
        </Card>
        <Card className="p-6">
          <h3 className="font-bold mb-3 flex items-center gap-2"><Swords size={16} /> Matches — Squad Selection</h3>
          {matches.length === 0 && <p className="text-sm text-muted-foreground">None scheduled</p>}
          {matches.slice(0, 10).map((m) => (
            <div key={m.id} className="flex justify-between items-center text-sm py-1.5 border-b border-border last:border-0">
              <span>vs {m.opponent} <span className="text-muted-foreground">· {m.date}</span></span>
              <Button size="sm" variant="outline" onClick={() => openSquad(m)} data-testid={`select-squad-${m.id}`}>Select Squad</Button>
            </div>
          ))}
        </Card>
      </div>

      <Dialog open={!!squad} onOpenChange={() => setSquad(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Squad — vs {squad?.match.opponent} ({squad?.match.date})</DialogTitle></DialogHeader>
          {squad && (squad.players.length === 0 ? <p className="text-sm text-muted-foreground">This match's team has no players.</p> : (
            <div className="space-y-2">
              {squad.players.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">{p.full_name}</span>
                  <Select value={squad.sel[p.id] || ""} onValueChange={(v) => setSquad((s) => ({ ...s, sel: { ...s.sel, [p.id]: v } }))}>
                    <SelectTrigger className="w-40" data-testid={`squad-status-${p.id}`}><SelectValue placeholder="Set status" /></SelectTrigger>
                    <SelectContent>{SEL_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          ))}
          <DialogFooter><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveSquad} data-testid="squad-save">Save & Notify Parents</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
